<div class="bg-night text-white max-h-[90vh] overflow-hidden overflow-x-hidden flex flex-col">
  <div class="p-4 lg:p-6 border-b border-white/10 flex items-center justify-between">
    <div class="flex items-center gap-3">
      <div class="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
        <span class="material-symbols-outlined text-green-400 text-2xl">check_circle</span>
      </div>
      <div>
        <h2 class="text-lg lg:text-xl font-bold">{{ 'pos.session_info.close_success_title' | translate }}</h2>
        <p class="text-xs text-white/50">{{ session.name || session.id }}</p>
      </div>
    </div>
    <button
      type="button"
      (click)="close()"
      class="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center">
      <span class="material-symbols-outlined">close</span>
    </button>
  </div>

  <div class="flex-1 overflow-y-auto p-4 lg:p-6 space-y-6">
    <div class="bg-white/5 rounded-xl p-4 space-y-3">
      <h3 class="text-sm font-semibold text-white flex items-center gap-2">
        <span class="material-symbols-outlined text-base text-neon">euro</span>
        {{ 'pos.session_info.stats.money' | translate }}
      </h3>
      <div class="grid grid-cols-2 gap-3">
        <div>
          <p class="text-xs text-white/50 mb-1">{{ 'pos.session_info.stats.cash' | translate }}</p>
          <p
            class="text-lg font-bold text-green-400"
            data-testid="close-stats-cash">
            {{ stats.cashTotalCents / 100 | number: '1.2-2' }} €</p
          >
        </div>
        <div>
          <p class="text-xs text-white/50 mb-1">{{ 'pos.session_info.stats.card' | translate }}</p>
          <p
            class="text-lg font-bold text-blue-400"
            data-testid="close-stats-card">
            {{ stats.cardTotalCents / 100 | number: '1.2-2' }} €</p
          >
        </div>
        @if (stats.pointsValueCents > 0 || stats.totalSpentPoints > 0) {
          <div class="col-span-2">
            <p class="text-xs text-white/50 mb-1">{{ 'pos.session_info.stats.points' | translate }}</p>
            <p
              class="text-lg font-bold text-amber-400"
              data-testid="close-stats-points-value">
              {{ stats.pointsValueCents / 100 | number: '1.2-2' }} €
              @if (stats.totalSpentPoints > 0) {
                <span class="text-xs text-white/50 font-medium">/ {{ stats.totalSpentPoints }} b.</span>
              }
            </p>
          </div>
        }
      </div>
      <div class="pt-3 border-t border-white/10">
        <div class="flex items-center justify-between">
          <p class="text-sm text-white/70">{{ 'pos.session_info.stats.total_money' | translate }}</p>
          <p
            class="text-xl font-bold text-neon"
            data-testid="close-stats-total-revenue">
            {{ stats.totalRevenueCents / 100 | number: '1.2-2' }} €</p
          >
        </div>
        @if (stats.pointsValueCents > 0) {
          <div class="flex items-center justify-between mt-1">
            <p class="text-xs text-white/50">{{ 'pos.session_info.stats.paid_by_points' | translate }}</p>
            <p class="text-xs font-semibold text-amber-300">
              {{ stats.pointsValueCents / 100 | number: '1.2-2' }} €
              @if (stats.totalSpentPoints > 0) {
                <span class="text-white/50 font-medium">({{ stats.totalSpentPoints }} b.)</span>
              }
            </p>
          </div>
        }
        @if (stats.netRevenueCents !== stats.totalRevenueCents) {
          <div class="flex items-center justify-between mt-1">
            <p class="text-xs text-white/50">{{ 'pos.session_info.stats.net_revenue' | translate }}</p>
            <p
              class="text-sm font-semibold text-neon/70"
              data-testid="close-stats-net-revenue">
              {{ stats.netRevenueCents / 100 | number: '1.2-2' }} €</p
            >
          </div>
        }
      </div>
    </div>

    @if (hasTabPaymentBreakdown) {
      <div class="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4 space-y-3">
        <h3 class="text-sm font-semibold text-white flex items-center gap-2">
          <span class="material-symbols-outlined text-base text-orange-400">payments</span>
          {{ 'pos.session_info.stats.tab_payment_breakdown' | translate }}
        </h3>

        <div class="pt-1 space-y-2 text-sm">
          <div class="flex items-center justify-between">
            <span class="text-white/70">{{ 'pos.session_info.stats.total_paid' | translate }}</span>
            <span
              class="font-semibold text-orange-300"
              data-testid="close-stats-tab-payments-total"
              >{{ tabPaymentBreakdown.totalPaidCents / 100 | number: '1.2-2' }} €</span
            >
          </div>
          <div class="flex items-center justify-between text-xs">
            <span class="text-white/60">{{ 'pos.session_info.stats.sales_in_session' | translate }}</span>
            <span class="font-medium text-white"
              >{{ tabPaymentBreakdown.currentSessionPaidCents / 100 | number: '1.2-2' }} €</span
            >
          </div>
          <div class="flex items-center justify-between text-xs">
            <span class="text-white/60">{{ 'pos.session_info.stats.repayments_prior_sessions' | translate }}</span>
            <span class="font-medium text-white"
              >{{ tabPaymentBreakdown.priorSessionsPaidCents / 100 | number: '1.2-2' }} €</span
            >
          </div>
        </div>
      </div>
    }

    @if (stats.overheadTotalCents > 0 || overheadItemsSorted.length > 0) {
      <div class="bg-red-500/10 border border-red-500/20 rounded-xl p-4 space-y-3">
        <h3 class="text-sm font-semibold text-white flex items-center gap-2">
          <span class="material-symbols-outlined text-base text-red-400">trending_down</span>
          {{ 'pos.session_info.stats.overhead' | translate }}
        </h3>

        <div class="flex items-center justify-between">
          <span class="text-sm text-white/70">{{ 'pos.session_info.stats.total' | translate }}</span>
          <span
            class="text-lg font-bold text-red-300"
            data-testid="close-stats-overhead-total">
            {{ stats.overheadTotalCents / 100 | number: '1.2-2' }} €</span
          >
        </div>

        @if (overheadItemsSorted.length > 0) {
          <div class="pt-3 border-t border-white/10">
            <p class="text-xs text-white/60 mb-3">{{ 'pos.session_info.stats.overhead_items' | translate }}</p>
            <div class="overflow-x-auto">
              <table class="w-full text-sm">
                <thead>
                  <tr class="border-b border-white/10">
                    <th class="text-left py-2 text-white/70 font-medium">
                      {{ 'pos.session_info.stats.posted_at' | translate }}
                    </th>
                    <th class="text-left py-2 text-white/70 font-medium">
                      {{ 'pos.session_info.stats.product' | translate }}
                    </th>
                    <th class="text-right py-2 text-white/70 font-medium">
                      {{ 'pos.session_info.stats.qty' | translate }}
                    </th>
                    <th class="text-right py-2 text-white/70 font-medium">
                      {{ 'pos.session_info.stats.total' | translate }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  @for (item of overheadItemsSorted; track item.orderId + ':' + item.productId + ':' + item.postedAt) {
                    <tr class="border-b border-white/5">
                      <td class="py-2 text-white/80 whitespace-nowrap">
                        <div class="flex flex-col">
                          <span>{{ item.postedAt | date: 'short' }}</span>
                          <span class="text-xs text-white/40">#{{ item.orderId | slice: 0 : 8 }}</span>
                        </div>
                      </td>
                      <td class="py-2 text-white">{{ item.productName }}</td>
                      <td class="py-2 text-right text-white/80">{{ item.qty }}</td>
                      <td class="py-2 text-right text-red-200 font-semibold">
                        {{ item.totalCents / 100 | number: '1.2-2' }} €
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        }
      </div>
    }

    @if (writeOffItems.length > 0) {
      <div class="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4 space-y-3">
        <h3 class="text-sm font-semibold text-white flex items-center gap-2">
          <span class="material-symbols-outlined text-base text-purple-400">money_off</span>
          {{ 'pos.session_info.stats.write_offs' | translate }}
        </h3>

        <div class="flex items-center justify-between">
          <span class="text-sm text-white/70">{{ 'pos.session_info.stats.total' | translate }}</span>
          <span
            class="text-lg font-bold text-purple-300"
            data-testid="close-stats-writeoff-total">
            {{ writeOffTotalCents / 100 | number: '1.2-2' }} €</span
          >
        </div>

        <div class="pt-3 border-t border-white/10">
          <p class="text-xs text-white/60 mb-3">{{ 'pos.session_info.stats.write_off_items' | translate }}</p>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-white/10">
                  <th class="text-left py-2 text-white/70 font-medium">
                    {{ 'pos.session_info.stats.write_off_tab' | translate }}
                  </th>
                  <th class="text-left py-2 text-white/70 font-medium">
                    {{ 'pos.session_info.stats.write_off_reason' | translate }}
                  </th>
                  <th class="text-right py-2 text-white/70 font-medium">
                    {{ 'pos.session_info.stats.total' | translate }}
                  </th>
                </tr>
              </thead>
              <tbody>
                @for (item of writeOffItems; track item.id) {
                  <tr class="border-b border-white/5">
                    <td class="py-2 text-white">
                      <div class="flex flex-col">
                        <span>{{ item.tabName }}</span>
                        <span class="text-xs text-white/40">{{ item.createdAt | date: 'short' }}</span>
                      </div>
                    </td>
                    <td class="py-2 text-white/80">
                      <div class="flex flex-col">
                        <span>{{ item.reason }}</span>
                        <span class="text-xs text-white/40">{{ item.createdByName }}</span>
                      </div>
                    </td>
                    <td class="py-2 text-right text-purple-200 font-semibold">
                      {{ item.amountCents / 100 | number: '1.2-2' }} €
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    }

    @if (cashReconciliation) {
      <div class="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-xl p-4 space-y-4">
        <h3 class="text-sm font-semibold text-white flex items-center gap-2">
          <span class="material-symbols-outlined text-base text-blue-400">account_balance</span>
          {{ 'pos.session_info.stats.cash_reconciliation' | translate }}
        </h3>

        <div class="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p class="text-xs text-white/50 mb-1">{{ 'pos.session_info.stats.opening_cash' | translate }}</p>
            <p
              class="text-base font-bold text-white"
              data-testid="close-stats-opening-cash">
              {{ cashReconciliation.openingCashCents / 100 | number: '1.2-2' }} €
            </p>
          </div>
          <div>
            <p class="text-xs text-white/50 mb-1">{{ 'pos.session_info.stats.closing_cash' | translate }}</p>
            <p
              class="text-base font-bold text-white"
              data-testid="close-stats-closing-cash">
              {{ (cashReconciliation.closingCashCents || 0) / 100 | number: '1.2-2' }} €
            </p>
          </div>
        </div>

        <div class="pt-3 border-t border-white/10 space-y-2">
          <div class="flex items-center justify-between text-xs">
            <span class="text-white/60">{{ 'pos.session_info.stats.walk_in_cash' | translate }}</span>
            <span class="font-medium text-white">
              {{ cashReconciliation.walkInCashCents / 100 | number: '1.2-2' }} €
            </span>
          </div>
          <div class="flex items-center justify-between text-xs">
            <span class="text-white/60">{{ 'pos.session_info.stats.tab_cash' | translate }}</span>
            <span class="font-medium text-white">
              {{ cashReconciliation.tabCashCents / 100 | number: '1.2-2' }} €
            </span>
          </div>
          <div class="flex items-center justify-between text-xs pt-1 border-t border-white/5">
            <span class="text-white/70 font-medium">{{ 'pos.session_info.stats.total_received' | translate }}</span>
            <span class="font-bold text-white">
              {{ cashReconciliation.totalCashReceivedCents / 100 | number: '1.2-2' }} €
            </span>
          </div>
        </div>

        <div class="pt-3 border-t border-white/10">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm text-white/70">{{ 'pos.session_info.stats.expected_cash' | translate }}</span>
            <span
              class="text-lg font-bold text-white"
              data-testid="close-stats-expected-cash">
              {{ cashReconciliation.expectedCashCents / 100 | number: '1.2-2' }} €
            </span>
          </div>
          @if (
            cashReconciliation.cashDifferenceCents !== undefined && cashReconciliation.cashDifferenceCents !== null
          ) {
            <div
              class="mt-3 p-3 rounded-lg flex items-center justify-between border"
              [ngClass]="{
                'bg-green-500/20 border-green-500/40': cashReconciliation.cashDifferenceCents === 0,
                'bg-red-500/20 border-red-500/40': cashReconciliation.cashDifferenceCents < 0,
                'bg-yellow-500/20 border-yellow-500/40': cashReconciliation.cashDifferenceCents > 0,
              }">
              <div class="flex items-center gap-2">
                @if (cashReconciliation.cashDifferenceCents === 0) {
                  <span class="material-symbols-outlined text-green-400">check_circle</span>
                  <span class="text-sm font-medium text-green-400">{{
                    'pos.session_info.stats.cash_balanced' | translate
                  }}</span>
                } @else if (cashReconciliation.cashDifferenceCents < 0) {
                  <span class="material-symbols-outlined text-red-400">error</span>
                  <span class="text-sm font-medium text-red-400">{{
                    'pos.session_info.stats.cash_missing' | translate
                  }}</span>
                } @else {
                  <span class="material-symbols-outlined text-yellow-400">warning</span>
                  <span class="text-sm font-medium text-yellow-400">{{
                    'pos.session_info.stats.cash_surplus' | translate
                  }}</span>
                }
              </div>
              <span
                class="text-base font-bold"
                data-testid="close-stats-cash-difference"
                [ngClass]="{
                  'text-green-400': cashReconciliation.cashDifferenceCents === 0,
                  'text-red-400': cashReconciliation.cashDifferenceCents < 0,
                  'text-yellow-400': cashReconciliation.cashDifferenceCents > 0,
                }">
                {{ Math.abs(cashReconciliation.cashDifferenceCents) / 100 | number: '1.2-2' }} €
              </span>
            </div>
          }
        </div>

        @if (currentSessionTabsBalanceCents > 0 || priorSessionTabsBalanceCents > 0) {
          <div class="pt-3 border-t border-white/10 space-y-2">
            @if (currentSessionTabsBalanceCents > 0) {
              <div class="flex items-center justify-between">
                <span class="text-xs text-white/60">{{
                  'pos.session_info.stats.open_tabs_current_session' | translate
                }}</span>
                <span class="text-sm font-bold text-orange-400"
                  >{{ currentSessionTabsBalanceCents / 100 | number: '1.2-2' }} €</span
                >
              </div>
            }
            @if (priorSessionTabsBalanceCents > 0) {
              <div class="flex items-center justify-between">
                <span class="text-xs text-white/60">{{
                  'pos.session_info.stats.open_tabs_prior_sessions' | translate
                }}</span>
                <span class="text-sm font-bold text-orange-400/60"
                  >{{ priorSessionTabsBalanceCents / 100 | number: '1.2-2' }} €</span
                >
              </div>
            }
            <div class="flex items-center justify-between pt-2 border-t border-white/5">
              <span class="text-sm text-white/70 font-medium">{{
                'pos.session_info.stats.total_cash_in_drawer' | translate
              }}</span>
              <span class="text-lg font-bold text-blue-400"
                >{{
                  (cashReconciliation.expectedCashCents + currentSessionTabsBalanceCents) / 100 | number: '1.2-2'
                }}
                €</span
              >
            </div>
          </div>
        }
      </div>
    }

    <div class="bg-white/5 rounded-xl p-4 space-y-3">
      <h3 class="text-sm font-semibold text-white flex items-center gap-2">
        <span class="material-symbols-outlined text-base text-purple-400">receipt_long</span>
        {{ 'pos.session_info.stats.summary' | translate }}
      </h3>
      <div class="grid grid-cols-2 gap-3">
        <div>
          <p class="text-xs text-white/50 mb-1">{{ 'pos.session_info.stats.orders' | translate }}</p>
          <p
            class="text-xl font-bold text-white"
            data-testid="close-stats-orders"
            >{{ stats.orderCount }}</p
          >
        </div>
        <div>
          <p class="text-xs text-white/50 mb-1">{{ 'pos.session_info.stats.tabs' | translate }}</p>
          <p
            class="text-xl font-bold text-white"
            data-testid="close-stats-tabs"
            >{{ stats.tabCount }}</p
          >
        </div>
      </div>
    </div>

    @if (currentSessionTabs.length > 0) {
      <div class="bg-orange-500/10 border border-orange-500/20 rounded-xl overflow-hidden">
        <div class="px-4 py-3 bg-orange-500/10 border-b border-orange-500/20">
          <h3 class="text-sm font-semibold text-orange-400 flex items-center gap-2">
            <span class="material-symbols-outlined text-base">warning</span>
            {{ 'pos.session_info.stats.open_tabs_this_session' | translate }} ({{ currentSessionTabs.length }})
          </h3>
          <div class="flex items-center justify-between mt-1">
            <span class="text-xs text-white/50">{{ 'pos.session_info.stats.unpaid_balance' | translate }}</span>
            <span
              class="text-xs font-bold text-orange-400"
              data-testid="close-stats-open-tabs-balance"
              >{{ currentSessionTabsBalanceCents / 100 | number: '1.2-2' }} €</span
            >
          </div>
        </div>
        <div class="max-h-[200px] overflow-y-auto">
          @for (tab of currentSessionTabs; track tab.id) {
            <ng-container *ngTemplateOutlet="tabRow; context: { $implicit: tab }" />
          }
        </div>
      </div>
    }

    @if (priorSessionTabs.length > 0) {
      <div class="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        <div class="px-4 py-3 bg-white/5 border-b border-white/10">
          <h3 class="text-sm font-semibold text-white/70 flex items-center gap-2">
            <span class="material-symbols-outlined text-base text-white/40">history</span>
            {{ 'pos.session_info.stats.open_tabs_prior_session' | translate }} ({{ priorSessionTabs.length }})
          </h3>
          <div class="flex items-center justify-between mt-1">
            <span class="text-xs text-white/50">{{ 'pos.session_info.stats.unpaid_balance' | translate }}</span>
            <span class="text-xs font-bold text-white/60"
              >{{ priorSessionTabsBalanceCents / 100 | number: '1.2-2' }} €</span
            >
          </div>
        </div>
        <div class="max-h-[200px] overflow-y-auto">
          @for (tab of priorSessionTabs; track tab.id) {
            <ng-container *ngTemplateOutlet="tabRow; context: { $implicit: tab }" />
          }
        </div>
      </div>
    }

    <ng-template
      #tabRow
      let-tab>
      <div class="px-4 py-3 border-b border-white/5 last:border-b-0">
        <div class="flex items-center justify-between mb-1">
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium text-white">
              @if (tab.type === PosTabType.User && tab.userName) {
                {{ tab.userName }}
              } @else if (tab.visitorAlias) {
                {{ tab.visitorAlias }}
              } @else {
                Tab #{{ tab.id.slice(0, 8) }}
              }
            </span>
            <span class="px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400 text-[10px] font-medium">
              {{
                tab.type === PosTabType.User
                  ? ('pos.tabs.type.user' | translate)
                  : ('pos.tabs.type.visitor' | translate)
              }}
            </span>
          </div>
          <span class="text-sm font-bold text-orange-400">
            {{ tab.balanceCents / 100 | number: '1.2-2' }} €
            @if (tab.balancePoints > 0) {
              / {{ tab.balancePoints }} b.
            }
          </span>
        </div>
        <p class="text-xs text-white/50">
          {{ tab.itemsCount }} {{ 'pos.session_info.stats.items' | translate }} ·
          {{ tab.createdAt | date: 'short' }}
        </p>
      </div>
    </ng-template>
  </div>

  <div class="p-4 lg:p-6 border-t border-white/10">
    <button
      type="button"
      data-testid="close-session-stats-ok"
      (click)="close()"
      class="w-full py-3 px-6 rounded-full bg-neon text-night font-bold hover:bg-neon/80 transition-all flex items-center justify-center gap-2 uppercase">
      <span class="material-symbols-outlined">check</span>
      {{ 'pos.session_info.close_stats_ok' | translate }}
    </button>
  </div>
</div>
